import React from 'react';
import { Alert, AlertTitle, type SxProps, type Theme } from '@mui/material';

import { usePatientInfoQuery } from '../../store/api';

import { describeDoseSpotSyncFailure } from './dosespotSyncCopy';

interface DoseSpotSyncBannerProps {
  patientId: string;
  /** Extra styles for surface-specific spacing (e.g. page vs. sidebar). */
  sx?: SxProps<Theme>;
}

/**
 * Warns that a patient's demographics stopped syncing to DoseSpot.
 *
 * Self-contained: it reads the failure reason from the patient `/info`
 * endpoint, so it can be dropped onto any surface (the legacy and server-driven
 * patient pages both use it) without threading data through. Renders nothing
 * when the patient is syncing normally or is not connected to DoseSpot, and
 * self-clears once a later push succeeds.
 */
export const DoseSpotSyncBanner: React.FC<DoseSpotSyncBannerProps> = ({ patientId, sx }) => {
  const { data } = usePatientInfoQuery({ patientId }, { skip: !patientId });

  const reason = data?.dosespot_demographics_sync_failure_reason;
  if (!reason) return null;

  const { headline, detail } = describeDoseSpotSyncFailure(reason);

  return (
    <Alert severity="warning" sx={sx}>
      <AlertTitle>{headline}</AlertTitle>
      {detail}
    </Alert>
  );
};
